<template>
  <WasteTypeView
    :waste-types="wasteTypes"
    :loading="loading"
    :error="error"
    :show-modal="showModal"
    :show-delete-modal="showDeleteModal"
    :is-edit-mode="isEditMode"
    :form-data="formData"
    :form-errors="formErrors"
    :delete-target="deleteTarget"
    @show-add-modal="showAddModal"
    @show-edit-modal="showEditModal"
    @confirm-delete="confirmDelete"
    @add-type="handleAddType"
    @edit-type="handleEditType"
    @delete-type="handleDeleteType"
    @close-modal="closeModal"
    @close-delete-modal="closeDeleteModal"
    @back-to-menu="handleBackToMenu"
    @refresh="handleRefresh"
    @submit-form="handleFormSubmit"
    @table-action="handleTableAction"
    @update:form-data="updateFormData"
  />
</template>

<script setup>
import { ref, onMounted } from 'vue'
import WasteTypeView from '../components/wasteType/WasteTypeView.vue'
import {
  getWasteTypes,
  createWasteType,
  updateWasteType,
  deleteWasteType
} from '@/services/systemSettings/wasteTypeService.js'

const emit = defineEmits(['show-success', 'back-to-menu'])

const wasteTypes = ref([])
const loading = ref(false)
const error = ref(null)

const showModal = ref(false)
const showDeleteModal = ref(false)
const isEditMode = ref(false)

const formData = ref({
  typeName: ''
})
const formErrors = ref({})
const deleteTarget = ref(null)

const updateFormData = (val) => {
  formData.value = val
}

const resetForm = () => {
  formData.value = {
    typeName: ''
  }
  formErrors.value = {}
}

const showAddModal = () => {
  isEditMode.value = false
  resetForm()
  showModal.value = true
}

const showEditModal = (type) => {
  isEditMode.value = true
  formData.value = { ...type }
  formErrors.value = {}
  showModal.value = true
}

const confirmDelete = (type) => {
  deleteTarget.value = type
  showDeleteModal.value = true
}

const closeModal = () => {
  resetForm()
  showModal.value = false
}

const closeDeleteModal = () => {
  deleteTarget.value = null
  showDeleteModal.value = false
}

const loadWasteTypes = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await getWasteTypes()

    if (response.success) {
      wasteTypes.value = response.data
    } else {
      error.value = response.message || 'ごみ種別データの取得に失敗しました'
    }
  } catch (err) {
    console.error('Failed to load waste types:', err)
    error.value = 'データの取得中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleAddType = async (typeData) => {
  loading.value = true
  error.value = null

  try {
    const response = await createWasteType(typeData)

    if (response.success) {
      wasteTypes.value = response.data
      closeModal()
      emit('show-success', response.message || 'ごみ種別が正常に追加されました')
    } else {
      error.value = response.message || 'ごみ種別の追加に失敗しました'
    }
  } catch (err) {
    console.error('Failed to add waste type:', err)
    error.value = 'ごみ種別の追加中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleEditType = async (typeData) => {
  loading.value = true
  error.value = null

  try {
    const response = await updateWasteType(typeData.id, typeData)

    if (response.success) {
      wasteTypes.value = response.data
      closeModal()
      emit('show-success', response.message || 'ごみ種別が正常に更新されました')
    } else {
      error.value = response.message || 'ごみ種別の更新に失敗しました'
    }
  } catch (err) {
    console.error('Failed to update waste type:', err)
    error.value = 'ごみ種別の更新中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleDeleteType = async (type) => {
  loading.value = true
  error.value = null

  try {
    const response = await deleteWasteType(type.id)

    if (response.success) {
      wasteTypes.value = response.data
      closeDeleteModal()
      emit('show-success', response.message || 'ごみ種別が正常に削除されました')
    } else {
      error.value = response.message || 'ごみ種別の削除に失敗しました'
    }
  } catch (err) {
    console.error('Failed to delete waste type:', err)
    error.value = 'ごみ種別の削除中にエラーが発生しました'
  } finally {
    loading.value = false
  }
}

const handleBackToMenu = () => {
  emit('back-to-menu')
}

const handleRefresh = async () => {
  await loadWasteTypes()
}

const handleFormSubmit = () => {
  if (!formData.value.typeName?.trim()) {
    formErrors.value = { typeName: '種別名は必須です' }
    return
  }
  if (isEditMode.value) {
    handleEditType(formData.value)
  } else {
    handleAddType(formData.value)
  }
}

const handleTableAction = ({ action, data }) => {
  if (action === 'edit') {
    showEditModal(data)
  } else if (action === 'delete') {
    confirmDelete(data)
  }
}

const loadInitialData = async () => {
  await loadWasteTypes()
}

onMounted(async () => {
  await loadInitialData()
})
</script>
